<template>
    <div>
        <!-- Form for editing details -->
        <form
            ref="auditForm"
            @submit.prevent="submitForm"
            class="d-flex flex-column form-container"
        >
            <!-- Audit Name -->
            <Field
                class="mt-0"
                :label="$t('audits.panels.summaryForm.auditName')"
                v-model="localAudit.name"
                type="text"
                :disabled="isTemplateSelectionLocked"
                :required="true"
            />

            <!-- Audit Type - Editable at any time -->
            <div>
                <label
                    class="mb-1 col-form-label d-flex justify-content-between align-items-center"
                >
                    {{ $t('audits.panels.summaryForm.type') }}
                    <ToolTip>
                        <template #tooltip-trigger>
                            <i
                                class="fi fi-info"
                                :aria-label="$t('common.iconButtons.info')"
                            ></i>
                        </template>
                        <template #tooltip-content>
                            {{ $t('audits.panels.summaryForm.typeHelp') }}
                        </template>
                    </ToolTip>
                </label>
                <Field
                    v-model="localAudit.audit_type"
                    type="radio"
                    :required="true"
                    :params="{
                        options: [
                            {
                                label: $t('audits.panels.summaryForm.audit'),
                                value: 'AUDIT',
                            },
                            {
                                label: $t(
                                    'audits.panels.summaryForm.assessment'
                                ),
                                value: 'ASSESSMENT',
                            },
                            {
                                label: $t('audits.panels.summaryForm.report'),
                                value: 'REPORT',
                            },
                        ],
                        name: 'audit-summary-type',
                    }"
                    :disabled="isTemplateSelectionLocked"
                />
            </div>

            <!-- Site -->
            <SiteDropdownField
                :label="$t('audits.panels.summaryForm.site')"
                type="select"
                v-model="localAudit.site_id"
                :required="true"
                :options="siteOptionsForTemplate"
                :disabled="isSiteFieldDisabled"
                :disabled-tooltip="disabledSiteTooltip"
                :placeholder="
                    hasTemplateSites && siteOptions.length === 0
                        ? $t('audits.panels.summaryForm.noTemplateSites')
                        : $t('audits.panels.summaryForm.sitePlaceholder')
                "
            >
                <span class="text-muted" style="font-size: 10pt">
                    {{ $t('audits.panels.summaryForm.siteVisibilityHint') }}
                </span>
            </SiteDropdownField>

            <!-- Auditor -->
            <Field
                :label="$t('audits.panels.summaryForm.auditor')"
                v-model="localAudit.auditor_id"
                type="select"
                :required="true"
                :disabled="!localAudit.site_id"
                :params="{
                    options: auditorOptions,
                    placeholder: localAudit.site_id
                        ? $t('audits.panels.summaryForm.auditorPlaceholder')
                        : $t('audits.panels.summaryForm.siteFirstPlaceholder'),
                    searchable: true,
                    allowEmpty: true,
                }"
            />
            <Field
                v-if="isAdminRoute && !isDashboardPerformDraft"
                :label="$t('audits.panels.summaryForm.reviewer')"
                v-model="localAudit.reviewed_by_id"
                type="select"
                :params="{
                    options: reviewerOptions,
                    placeholder: $t(
                        'audits.panels.summaryForm.reviewerPlaceholder'
                    ),
                    searchable: true,
                    allowEmpty: true,
                }"
            />

            <!-- Area -->
            <div class="area-container">
                <div style="flex: 1">
                    <Field
                        :label="$t('audits.panels.summaryForm.area')"
                        v-model="localAudit.area_id"
                        :required="true"
                        :disabled="areaOptions.length < 1"
                        type="select"
                        :params="{
                            options: areaOptions,
                            placeholder: $t(
                                'audits.panels.summaryForm.areaPlaceholder'
                            ),
                            searchable: true,
                            allowEmpty: true,
                        }"
                        style="width: 100%"
                    />
                </div>
                <button
                    v-if="!auditAreas?.length"
                    type="button"
                    class="btn btn-link p-0 area-edit-link"
                    @click="openAreaForm"
                >
                    Edit
                </button>
            </div>

            <!-- Score -->
            <div v-if="!isDashboardPerformDraft" class="field-container">
                <Field
                    v-if="
                        localAudit.score_override != null ||
                        !(localAudit.form_max_score > 0)
                    "
                    :label="$t('audits.panels.summaryForm.auditScore')"
                    v-model="localAudit.score_override"
                    :required="false"
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                />
            </div>

            <!-- Date -->
            <Field
                :label="$t('audits.panels.summaryForm.date')"
                v-model="localAudit.date"
                type="date"
                :required="true"
                :params="{ maxDate: today }"
            />

            <!-- Note -->
            <Field
                :label="$t('audits.panels.summaryForm.note')"
                v-model="localAudit.details"
                type="textarea"
            />

            <!-- File Upload -->
            <template v-if="!isDashboardPerformDraft">
                <small
                    style="
                        font-weight: 600;
                        color: var(--neutral-900);
                        line-height: 100%;
                    "
                    >{{ $t('audits.panels.summaryForm.uploadFile') }}</small
                >

                <div class="image-card mt-2">
                    <AdvancedUploadField
                        :multiple="true"
                        :accept="['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']"
                        v-model="localAudit.files"
                    />
                </div>
            </template>

            <!-- Send notification checkbox - only show in admin route for new entries -->
            <div v-if="isAdminRoute && newEntry">
                <Field
                    :label="$t('audits.panels.summaryForm.sendNotification')"
                    v-model="sendNotification"
                    type="checkbox"
                />
                <span class="text-muted fst-italic" style="font-size: 10pt">
                    {{ $t('audits.panels.summaryForm.notificationHint') }}
                </span>
            </div>
        </form>
    </div>
</template>

<script>
import moment from 'moment';
import Field from '../Field.vue';
import AdvancedUploadField from '../fields/AdvancedUploadField.vue';
import ToolTip from '../fields/ToolTip.vue';
import SiteDropdownField from '../fields/SiteDropdownField.vue';
import { useSiteStore } from '@/stores/site';
import { useAreaStore } from '@/stores/area';
import { getDefaultSitesForNewRecord } from '@/utils/SiteAccessUtils';
import { getUserOptions } from '@/utils/userOptions';
import { isAuditDraft } from '@/utils/auditStatus';

export default {
    name: 'AuditSummaryForm',
    components: {
        AdvancedUploadField,
        Field,
        ToolTip,
        SiteDropdownField,
    },
    props: {
        audit: {
            type: Object,
            default: () => ({}),
        },
        newEntry: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            files: [],
            localAudit: {},
            auditSites: [],
            siteStore: null,
            auditAreas: [],
            areaStore: null,
            adminCompanySites: [],
            adminCompanyAreas: [],
            sendNotification: false,
            auditorOptions: [],
            reviewerOptions: [],
        };
    },
    computed: {
        isAdminRoute() {
            return this.$route.matched.some((r) => r.name === 'Admin');
        },
        today() {
            return new Date();
        },
        areaOptions() {
            if (this.auditAreas?.length > 0) {
                return this.auditAreas;
            }

            if (this.isAdminRoute && this.adminCompanyAreas?.length > 0) {
                return this.adminCompanyAreas;
            }

            if (!this.areaStore || !this.areaStore.getAreaOptions) {
                return [];
            }

            return this.areaStore.getAreaOptions.map((opt) => ({
                label: opt.label ?? opt.text,
                value: opt.value,
            }));
        },
        defaultSiteId() {
            // For existing audits, use their site_id
            if (!this.newEntry && this.audit?.site_id) {
                return this.audit.site_id;
            }

            // For new audits, use view-based defaults
            const defaultSites = getDefaultSitesForNewRecord({
                viewType: this.$siteStore?.viewType,
                globalSiteId: this.$siteStore?.globalSiteId,
                userSites: this.$siteStore?.userSiteIds || [],
            });

            // Return first site (single-select)
            return defaultSites.length > 0 ? defaultSites[0] : '';
        },
        siteOptions() {
            const storeOptions =
                this.isAdminRoute && this.adminCompanySites?.length > 0
                    ? this.adminCompanySites
                    : this.siteStore?.getSiteOptions || [];
            const auditIds = new Set(
                this.auditSites?.map((s) => Number(s.value)) || []
            );
            const userIds = new Set(
                this.$siteStore?.userSiteIds?.map((id) => Number(id)) || []
            );

            return storeOptions.filter((opt) => {
                const id = Number(opt.value);

                // must be in auditSites if any are defined
                const auditOk = auditIds.size === 0 || auditIds.has(id);

                // must be in userSites if not admin and any are defined
                const userOk =
                    this.isAdminRoute || userIds.size === 0 || userIds.has(id);

                return auditOk && userOk;
            });
        },
        // Check if template defines specific sites
        hasTemplateSites() {
            return !!this.auditSites?.length;
        },
        // Determine if site field should be disabled
        isSiteFieldDisabled() {
            return (
                this.$siteStore?.isSelectedSiteView || !this.siteOptions?.length
            );
        },
        disabledSiteTooltip() {
            // Only show a tooltip when the field is actually disabled
            if (!this.isSiteFieldDisabled) {
                return '';
            }

            if (this.$siteStore?.isSelectedSiteView) {
                return this.$t(
                    'audits.panels.scheduleForm.disabled.siteViewRequired'
                );
            }

            if (!this.siteOptions?.length) {
                return this.$t(
                    'audits.panels.templateContent.noSitesAvailable'
                );
            }
            return '';
        },
        // Provide filtered options to SiteDropdownField
        siteOptionsForTemplate() {
            // Always pass explicit options so admin flows can use company-scoped lists.
            return this.siteOptions;
        },
        isTemplateSelectionLocked() {
            return (
                Boolean(this.localAudit.template_id) || isAuditDraft(this.audit)
            );
        },
        isDashboardPerformDraft() {
            return isAuditDraft(this.audit) && Boolean(this.audit?.template_id);
        },
    },
    watch: {
        'localAudit.site_id': {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    this.loadAuditorOptions(newValue);
                    this.loadReviewerOptions(newValue);
                } else {
                    this.auditorOptions = [];
                    this.localAudit.auditor_id = null;
                    this.reviewerOptions = [];
                    this.localAudit.reviewed_by_id = null;
                }
            },
        },
        'localAudit.company_id': {
            immediate: true,
            handler() {
                this.loadAdminCompanyOptions();
            },
        },
    },
    emits: ['submit', 'open-modal', 'areas'],
    methods: {
        async loadAuditorOptions(siteId = null) {
            if (!siteId) {
                this.auditorOptions = [];
                return;
            }

            const options = (await getUserOptions(this, [siteId])) || [];
            this.auditorOptions = options;
        },
        async loadReviewerOptions(siteId = null) {
            const options =
                (await getUserOptions(this, siteId ? [siteId] : null)) || [];
            this.reviewerOptions = options;
        },
        openAreaForm() {
            this.$emit('areas');
        },
        resetForm() {
            const today = new Date().toISOString();
            this.localAudit = {
                id: !this.newEntry ? this.audit?.id : undefined,
                template_id: this.audit?.template_id || undefined,
                company_id: this.audit?.company_id || null,
                form_max_score: this.audit?.form_max_score ?? undefined,
                name: this.audit?.name || '',
                site_id: this.defaultSiteId,
                date: this.audit?.date
                    ? moment.utc(this.audit.date).format('YYYY-MM-DD')
                    : this.newEntry
                    ? today
                    : '',
                auditor_id: this.audit?.auditor_id,
                reviewed_by_id: this.audit?.reviewed_by_id
                    ? String(this.audit.reviewed_by_id)
                    : null,
                area_id: this.audit?.area_id || '',
                details: this.audit?.details || '',
                score_override: this.audit?.score_override ?? null,
                files: this.audit?.files ? this.audit.files : [],
                audit_type: this.audit?.audit_type || null,
            };
        },
        submitForm() {
            const form = this.$refs.auditForm;

            // 1) run native validation check
            if (!form.checkValidity()) {
                form.reportValidity();

                return; // stop here—don't save
            }
            // Prepare form data by transforming files into an array of file IDs
            const preparedData = this.prepareFormDataForSubmission();
            this.$emit(
                'submit',
                preparedData,
                this.newEntry,
                this.sendNotification
            );
        },
        prepareFormDataForSubmission() {
            // Only record completed_at if not already completed
            if (this.newEntry) {
                this.localAudit.completed_at = Date.now();
            }
            // Deep clone formData to avoid mutating the original object
            const formDataToSubmit = JSON.parse(
                JSON.stringify(this.localAudit)
            );

            if (
                formDataToSubmit.company_id === null ||
                formDataToSubmit.company_id === ''
            ) {
                delete formDataToSubmit.company_id;
            }

            // Transform files: extract file IDs if any exist
            if (formDataToSubmit.files && formDataToSubmit.files.length > 0) {
                formDataToSubmit.files = formDataToSubmit.files.map(
                    (file) => file.id
                );
            }

            // Set app_admin_only to true when saving from admin panel
            if (
                this.$route.name === 'Admin' ||
                this.$route.name === 'AdminPanel' ||
                this.$route.name === 'Company Detail' ||
                this.$route.matched.some((r) => r.name === 'Admin')
            ) {
                formDataToSubmit.app_admin_only = true;
            }

            return formDataToSubmit;
        },
        getEndpointForCategory(category, isNewEntry) {
            // Use single audit endpoint for all categories
            const action = isNewEntry ? 'create' : 'update';
            return `audit/${action}`;
        },
        loadAuditTemplateOptions() {
            const params = {
                where: { id: Number(this.localAudit.template_id) },
                fields: ['id'],
                include: {
                    sites: {
                        fields: ['id', 'name'],
                    },
                    areas: {
                        fields: ['id', 'name'],
                    },
                },
            };
            const companyId =
                this.audit?.company_id || this.localAudit?.company_id;
            if (companyId) {
                params.company_id = Number(companyId);
            }
            this.$getData('audit/template/read', params).then((response) => {
                if (response?.success && response.data) {
                    const { id, sites, areas } = response.data?.[0];

                    this.auditSites = this.populateOptions(
                        sites,
                        this.localAudit.site_id,
                        this.siteStore?.getSiteOptions
                    );

                    this.auditAreas = this.populateOptions(
                        areas,
                        this.localAudit.area_id,
                        this.areaStore?.getAreaOptions
                    );
                } else {
                    this.$alertStore.pushError(
                        response?.message ||
                            this.$t(
                                'audits.panels.templateContent.failedToLoadTemplate'
                            )
                    );
                }
            });
        },
        loadAdminCompanyOptions() {
            if (!this.isAdminRoute) {
                this.adminCompanySites = [];
                this.adminCompanyAreas = [];
                return;
            }

            const companyId =
                Number(this.localAudit?.company_id) ||
                Number(this.audit?.company_id);
            if (!companyId) {
                this.adminCompanySites = [];
                this.adminCompanyAreas = [];
                return;
            }

            this.$getData('company-site/read', { company_id: companyId }).then(
                (response) => {
                    if (response?.success && Array.isArray(response.data)) {
                        this.adminCompanySites = response.data.map((site) => ({
                            label: site.name,
                            value: String(site.id),
                        }));
                    }
                }
            );

            this.$getData('audit-area/read', { company_id: companyId }).then(
                (response) => {
                    if (response?.success && Array.isArray(response.data)) {
                        this.adminCompanyAreas = response.data.map((area) => ({
                            label: area.name,
                            value: String(area.id),
                        }));
                    }
                }
            );
        },
        populateOptions(rawOptions, currentId, storeOptions) {
            let options = rawOptions
                ? rawOptions.map((o) => ({
                      label: o.name,
                      value: String(o.id),
                  }))
                : [];

            if (options.length > 0 && currentId) {
                if (!options.some((o) => o.value === String(currentId))) {
                    const fromStore = storeOptions?.find(
                        (o) => String(o.value) === String(currentId)
                    );
                    if (fromStore) options.push(fromStore);
                }
            }

            return options;
        },
    },
    mounted() {
        this.resetForm();
        this.siteStore = useSiteStore();
        this.siteStore.syncSites();
        this.areaStore = useAreaStore();
        this.areaStore.syncAreas();
        this.loadAdminCompanyOptions();
        this.loadReviewerOptions(this.localAudit.site_id);

        if (this.audit?.template_id) {
            this.loadAuditTemplateOptions();
        }
    },
};
</script>

<style scoped>
.image-card {
    padding: var(--space-3);
    gap: 0.75rem;
    background-color: var(--neutral-400);
    border-radius: var(--space-3);
}

.field-container {
    position: relative;
    width: 100%;
}

.edit-area {
    cursor: pointer;
    color: var(--primary-400, #678277);
    font-family: Inter;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.125rem;
    /* 120% */
    text-decoration-line: underline;
    white-space: nowrap;
    margin: 1rem 0;
}

.score-display {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 0.875rem;
    line-height: 0.875rem;
    padding: 0 0.75rem;
}

.form-container {
    gap: var(--space-4);
}

.area-edit-link {
    cursor: pointer;
    color: var(--primary-400, #678277);
    font-family: Inter;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.125rem;
    text-decoration-line: underline;
    white-space: nowrap;
    margin-bottom: 0.625rem;
}

.area-edit-link:hover {
    color: var(--primary-400, #678277);
    background-color: transparent;
}

.area-container {
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
}
</style>
